import type { Schemas } from '@npc/api-client'
import { Link } from '@tanstack/react-router'

import { buttonClass } from '../../components/button-style'
import { Card, CardTitle } from '../../components/card'
import { StatusDot } from '../../components/tag'
import { T } from '../../copy/text'
import { ago, elapsed } from '../../app/format'

/**
 * 「上一次去看这台机器，成没成」。
 *
 * ## 它和「状态」那一格回答的不是同一个问题
 *
 * ```
 * srv01        reach=ok          status=offline    够得到，但没装探针
 * 10.62.0.33   reach=ok          status=online     装了探针
 * 10.62.0.32   reach=credential  status=offline    钥匙失效
 * ```
 *
 * **`status` 说的是探针连没连上，`reach` 说的是 SSH 够不够得到。两个都对。**
 * 「够得到但离线」不是矛盾，**是常态**——一台刚接进来、还没装探针的机器就长这样。
 *
 * 所以这两格**必须分开**，判据照旧：**用户看完之后要做的下一个动作不一样**。
 * `reach` 不 ok 是「有东西挡着 / 钥匙失效」，他要处理；
 * `status = offline` 而 `reach = ok` 是「探针没连上而机器好好的」，完全另一件事。
 *
 * ## 还没被看过的机器，这一块整个不画
 *
 * 契约把这条写死了，理由值得留着：**一个所有字段都是零值的对象，
 * 在界面上会被渲染成「够不到」，而事实是「还没去看过」。**
 * 所以判据是 `reach` 这个字段在不在，不是它里面的值——
 * 文案层刻意没有「还没看过」那一条，**那一档的正确表现就是什么都不渲染**。
 *
 * ## `stale` 的门槛在后端，这儿只负责说
 *
 * 前端不拿 `fail_streak` 或 `last_ok_at` 去算那个「两天」——**两边各算一份的话，
 * 改门槛那天只会改到一边**，而表现是界面上写着两天、后端按新门槛在报。
 * 判据只读 `reach.stale` 这个布尔，`{since}` 只负责把时长说成人话。
 */
export function Reach({
  reach,
  reconnectAddress,
}: {
  reach: Schemas['HostReach'] | undefined
  /** 钥匙不认了时的下一步：进加机器，只带地址，不回显密钥。 */
  reconnectAddress?: string
}) {
  // **不是给一个空壳**：没被看过就整块不出现
  if (!reach) return null

  const ok = reach.kind === 'ok'

  return (
    <Card>
      <CardTitle className="mb-npc-grid-sm flex items-center gap-npc-gap-sm">
        {/* `host_key_changed` 是唯一真要他动手的一档（契约：平台不替他判），
            所以只有它是红的。其余不 ok 的都是琥珀——有事，但不用他此刻决定什么 */}
        <StatusDot tone={ok ? 'ok' : reach.kind === 'host_key_changed' ? 'danger' : 'warn'} />
        <ReachLine reach={reach} />
      </CardTitle>

      {/* 连着够不到太久了。**门槛后端定**，这儿只把它说出来。
          `last_ok_at` 为空时不画这一行——那时「从来没成过」那句才是对的说法，
          而「没看到它了」前面挂一个空串是一句缺字的话 */}
      {reach.stale && reach.last_ok_at ? (
        <p className="m-0 text-npc-caption leading-[1.7] text-npc-amber-ink">
          <T id="host.reach.stale" vars={{ since: elapsed(reach.last_ok_at) }} />
        </p>
      ) : null}

      {/* 从来没看成过。**与「上次成功是很久以前」分开**（契约原话）——
          从来没成过多半是接进来那一步就没对上（地址、端口、跳板），
          不是「后来坏了」，他要回去看的地方不一样 */}
      {reach.never_succeeded ? (
        <p className="m-0 mt-npc-gap-xs text-npc-caption leading-[1.7] text-npc-ink-soft">
          <T id="host.reach.never_succeeded" />
        </p>
      ) : null}

      {/* 连着几次没成。**ok 的时候不说**——看成一次就归零，
          归了零还挂着上一次的数字，和「刚够到它就别再挂着上一次失败的戳」是同一件事 */}
      {!ok && reach.fail_streak > 1 ? (
        <p className="m-0 mt-npc-gap-xs text-npc-caption text-npc-ink-soft">
          <T id="host.reach.fail_streak" vars={{ count: reach.fail_streak }} />
        </p>
      ) : null}

      {/* 钥匙不认了必须有出口。详情连通块说完「进不去」就要能重新接一次。 */}
      {reach.kind === 'credential' && reconnectAddress ? (
        <div className="mt-npc-grid-sm">
          <Link
            to="/hosts/new"
            search={{ address: reconnectAddress }}
            className={buttonClass({ size: 'sm' })}
          >
            <T id="host.action.reconnect" />
          </Link>
        </div>
      ) : null}
    </Card>
  )
}

/**
 * 机器列表里那一行。**只在够不到的时候出现。**
 *
 * 列表不给 `ok` 留位置，理由是「沉默是好消息」：一列全是「20 分钟前看过它，
 * 没问题」，真正够不到的那一台反而看不出来了。详情页那张卡片是另一回事——
 * 那是他专门点进去看这台机器的地方，「没问题」在那儿是个答案。
 */
export function ReachNote({ reach }: { reach: Schemas['HostReach'] | undefined }) {
  if (!reach || reach.kind === 'ok') return null

  return (
    <span
      className={[
        'mt-[3px] block text-npc-caption leading-[1.6]',
        // 与详情页那张卡同一套着色：**只有指纹变了是红的**，
        // 因为只有那一档真要他自己动手确认
        reach.kind === 'host_key_changed' ? 'text-npc-red-ink' : 'text-npc-amber-ink',
      ].join(' ')}
    >
      <ReachLine reach={reach} />
    </span>
  )
}

function ReachLine({ reach }: { reach: Schemas['HostReach'] }) {
  switch (reach.kind) {
    case 'ok':
      return <T id="host.reach.ok" vars={{ when: ago(reach.last_try_at) }} />
    case 'blocked':
      return <T id="host.reach.blocked" />
    case 'credential':
      // **与 blocked 分开是这一组里最要紧的一件事**：他去查一整天防火墙，
      // 也不会让一把失效的钥匙重新生效
      return <T id="host.reach.credential" />
    case 'host_key_changed':
      return <T id="host.reach.host_key_changed" />
    default:
      // **刻意不并进 blocked**：说不清的时候说「说不清」，
      // 比随便挑个方向让他查一整天防火墙更省他的时间
      return <T id="host.reach.unknown" />
  }
}
